'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { countViewAndCheckEligible } from '@/utils/newsletter-popup-storage';

/** 데스크톱 3단 레이아웃(xl=1200) 미만이 모바일 — 팝업은 모바일에서만 뜬다 */
const MOBILE_QUERY = '(max-width: 1199px)';

/** 본문을 절반 읽은 지점 */
const SHOW_AT_PROGRESS = 0.5;

/**
 * 모바일 뉴스레터 팝업의 노출 시점을 정한다.
 *
 * 노출 조건 3개를 모두 만족해야 한다: 모바일 폭 · 이번 글이 노출 차례
 * (1·4·7... 번째, 구독자는 제외) · 스크롤 50% 도달.
 *
 * 차례 판정은 마운트당 한 번만 한다(호출이 조회 수를 올리므로).
 * 리스너는 차례일 때만 붙고, 뜨는 순간 떼어진다.
 */
export const useNewsletterPopupTrigger = (disabled: boolean) => {
  const [isEligible, setIsEligible] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const hasCountedRef = useRef(false);

  useEffect(() => {
    if (disabled || hasCountedRef.current) return;
    // StrictMode 이중 실행·리마운트에서 조회 수가 두 번 오르지 않게 막는다.
    // 판정 결과는 state 라 두 번째 실행에서도 유지된다.
    hasCountedRef.current = true;

    if (!window.matchMedia(MOBILE_QUERY).matches) return;

    setIsEligible(countViewAndCheckEligible());
  }, [disabled]);

  useEffect(() => {
    if (!isEligible) return;

    let frameId = 0;
    let isFrameQueued = false;

    const check = () => {
      isFrameQueued = false;
      const scrollable =
        document.documentElement.scrollHeight - window.innerHeight;
      // 스크롤이 불가능한 짧은 페이지에는 "50% 지점"이 없다
      if (scrollable <= 0) return;

      if (window.scrollY / scrollable >= SHOW_AT_PROGRESS) {
        setIsOpen(true);
        // isEligible 을 내려 이 effect 의 cleanup 으로 리스너를 떼어낸다
        setIsEligible(false);
      }
    };

    // 스크롤 이벤트마다 레이아웃을 읽지 않도록 프레임당 한 번으로 묶는다
    const handleScroll = () => {
      if (isFrameQueued) return;
      isFrameQueued = true;
      frameId = requestAnimationFrame(check);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    // 예약된 프레임도 취소한다 — 언마운트 직후 실행되면 사라진 컴포넌트에 setState 한다
    return () => {
      window.removeEventListener('scroll', handleScroll);
      cancelAnimationFrame(frameId);
    };
  }, [isEligible]);

  const close = useCallback(() => setIsOpen(false), []);

  return { isOpen, close };
};
